import type { NotificationType } from "../drizzle/schema";
import type { CandidateWaitingPopup } from "./candidateWaiting";

/**
 * Какие уведомления всплывают карточкой поверх экрана, а какие ждут в
 * колокольчике.
 *
 * Решение владелицы 21.09.2026: «не колокольчик, а чтобы хоп — уведомление
 * всплыло справа, и я по нему сразу перешла куда надо». Всплывать должно не
 * всё подряд: в базе больше 24 000 уведомлений, и если поверх экрана начнёт
 * появляться каждое, человек закроет их не глядя, а важное утонет вместе с
 * остальным.
 *
 * Правило отбора простое: всплывает то, после чего надо что-то СДЕЛАТЬ, и
 * чем раньше, тем лучше.
 *  • расшифровка готова — можно читать, за этим и ждали;
 *  • расшифровка сорвалась — запускать заново;
 *  • кандидат ждёт в комнате — войти в интервью;
 *  • кандидат отозвал согласие — звонить заказчику, у него погасла ссылка;
 *  • просят удалить запись — решать;
 *  • ответ на просьбу — узнать, стёрли или нет.
 *
 * Счета, дедлайны и назначения остаются в колокольчике: они важны, но ждут.
 *
 * Упоминаний в переписке здесь НЕТ, хотя по смыслу они просятся первыми.
 * Причина в замере 21.09.2026: владелица получает 1162 «упомянул(а) вас» в
 * месяц — по 39 в день, — потому что уведомление этого типа рассылается
 * владельцам на КАЖДЫЙ комментарий к любой вакансии, а не когда их правда
 * позвали (server/routers/kanban.ts). Всплывашка с таким потоком означала бы
 * 39 карточек со звуком в день. Вернуть сюда упоминания можно будет ровно
 * тогда, когда «упомянул(а) вас» снова будет значить, что человека позвали.
 */
export const POPUP_NOTIFICATION_TYPES: readonly NotificationType[] = [
  "video_candidate_waiting",
  "video_transcript_ready",
  "video_transcript_failed",
  "video_consent_withdrawn",
  "video_deletion_requested",
  "video_deletion_decided",
] as const;

/** Больше трёх карточек разом — это уже стена, а не уведомление. */
export const POPUP_MAX_VISIBLE = 3;

/** Сколько непрочитанных всплывашек вообще запрашиваем за раз. */
export const POPUP_FETCH_LIMIT = 10;

export type NotificationPopup = {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  link: string | null;
  createdAt: string;
};

/**
 * Всё, что всплывает, одним опросом: уведомления из базы и живые карточки
 * «кандидат ждёт вас в комнате», посчитанные из памяти сервера.
 */
export type NotificationPopupFeed = {
  items: NotificationPopup[];
  waiting: CandidateWaitingPopup[];
};

/** Сохранённое уведомление заменяет живую карточку той же комнаты. */
export function liveWaitingWithoutSavedNotice(
  waiting: readonly CandidateWaitingPopup[],
  items: readonly NotificationPopup[]
): CandidateWaitingPopup[] {
  const savedLinks = new Set(
    items
      .filter(item => item.type === "video_candidate_waiting")
      .map(item => item.link)
  );
  return waiting.filter(item => !savedLinks.has(item.link));
}
